import React from 'react';
import { ArrowLeft, Edit, Printer, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SavedAGB, agbProvider, generateAGBTitle, getIncompleteAGBFields, getEffectiveClauses, resolveAGBPlaceholders } from '@/lib/constants/agbTemplateData';
import { renderClauseContent } from '@/components/admin/avv/renderClauseContent';

interface AGBPreviewProps {
  agb: SavedAGB;
  onBack: () => void;
  onEdit: () => void;
  onPrint: () => void;
  onFinalize: () => void;
}

const billingModelLabels: Record<string, string> = {
  monthly: 'Monatlich',
  yearly: 'Jährlich',
  one_time_per_user: 'Einmal/Nutzer',
  credits_plus_fixed: 'Credits + Fix',
  cooperation: 'Kooperation',
};

const AGBPreview: React.FC<AGBPreviewProps> = ({ agb, onBack, onEdit, onPrint, onFinalize }) => {
  const incompleteFields = getIncompleteAGBFields(agb);
  const isIncomplete = incompleteFields.length > 0;
  const effectiveClauses = getEffectiveClauses(agb);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Zurück
          </Button>
          <Badge variant={agb.status === 'finalized' ? 'default' : 'secondary'}>
            {agb.status === 'finalized' ? 'Finalisiert' : 'Entwurf'}
          </Badge>
        </div>
        <div className="flex gap-2">
          {agb.status !== 'finalized' && (
            <>
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Edit className="h-4 w-4 mr-1" />
                Bearbeiten
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onFinalize}
                disabled={isIncomplete}
                title={isIncomplete ? 'Alle Pflichtfelder müssen ausgefüllt sein' : undefined}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Finalisieren
              </Button>
            </>
          )}
          <Button size="sm" onClick={onPrint}>
            <Printer className="h-4 w-4 mr-1" />
            Druckansicht / PDF
          </Button>
        </div>
      </div>

      {isIncomplete && agb.status !== 'finalized' && (
        <Alert variant="destructive" className="border-yellow-500 bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-200 dark:border-yellow-700 [&>svg]:text-yellow-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unvollständiger Entwurf</AlertTitle>
          <AlertDescription>
            Fehlende Angaben: {incompleteFields.join(', ')}.
            Die AGB können erst finalisiert werden, wenn alle Pflichtangaben vollständig sind.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="py-8 px-6 md:px-12 max-w-4xl mx-auto space-y-8">
          {/* Title */}
          <div className="text-center space-y-4">
            <h1 className="text-2xl font-bold">Allgemeine Geschäftsbedingungen</h1>
            <p className="text-lg">für SaaS-Dienste der PaperVerse-Plattform</p>
            <p className="text-muted-foreground">{generateAGBTitle(agb.customer.name)}</p>
          </div>

          <Separator />

          {/* Parties */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-2">Kunde (Auftraggeber)</h3>
              <div className="text-sm space-y-1">
                <p className="font-medium">{agb.customer.name || '[Kundenname]'}</p>
                <p>{agb.customer.address || '[Adresse]'}</p>
                {agb.customer.representedBy && <p>Vertreten durch: {agb.customer.representedBy}</p>}
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Anbieter (Auftragnehmer)</h3>
              <div className="text-sm space-y-1">
                <p className="font-medium">{agbProvider.name}</p>
                <p>{agbProvider.address}</p>
                <p>Vertreten durch: {agbProvider.representedBy}</p>
                <p>E-Mail: {agbProvider.email}</p>
              </div>
            </div>
          </div>

          {/* Service summary */}
          <div>
            <h3 className="font-semibold mb-2">Leistungsübersicht</h3>
            <div className="text-sm grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-muted-foreground">Seats</p>
                <p className="font-medium">{agb.serviceScope.seats}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Credits/Monat</p>
                <p className="font-medium">{agb.serviceScope.creditsPerMonth}</p>
              </div>
              <div>
                <p className="text-muted-foreground">API-Zugang</p>
                <p className="font-medium">{agb.serviceScope.apiAccess ? `Ja (${agb.serviceScope.apiRateLimit})` : 'Nein'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Preismodell</p>
                <p className="font-medium">{billingModelLabels[agb.commercialTerms.billingModel] || agb.commercialTerms.billingModel}</p>
              </div>
            </div>

            {/* Extra badges for institutional features */}
            <div className="flex flex-wrap gap-2 mt-3">
              {agb.contractTerms.tenantIsolation && (
                <Badge variant="outline">Mandantentrennung</Badge>
              )}
              {agb.contractTerms.customReviewCriteria && (
                <Badge variant="outline">Custom Review-Kriterien</Badge>
              )}
              {agb.contractTerms.activeConsentForChanges && (
                <Badge variant="outline">Aktive Zustimmung</Badge>
              )}
              {!agb.contractTerms.autoRenewal && (
                <Badge variant="outline">Festes Enddatum</Badge>
              )}
            </div>
          </div>

          <Separator />

          {/* Clauses */}
          {effectiveClauses.map(clause => {
            const content = resolveAGBPlaceholders(clause.content, agb);
            return (
              <div key={clause.id} className="space-y-2">
                <h2 className="text-lg font-bold">{clause.title}</h2>
                <div className="text-sm">{renderClauseContent(content)}</div>
                <Separator className="mt-4" />
              </div>
            );
          })}

          {/* Signatures */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-8">
            <div className="space-y-4">
              <p className="text-sm">Ort, Datum</p>
              <div className="border-b border-foreground w-full h-8" />
              <p className="text-sm font-medium">{agb.customer.name || '[Kundenname]'}</p>
              <p className="text-xs text-muted-foreground">(Kunde)</p>
            </div>
            <div className="space-y-4">
              <p className="text-sm">Ort, Datum</p>
              <div className="border-b border-foreground w-full h-8" />
              <p className="text-sm font-medium">{agbProvider.name}</p>
              <p className="text-xs text-muted-foreground">(Anbieter)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AGBPreview;
